import React, { useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, Alert, Paper
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import * as mammoth from 'mammoth';
import { callClaudeJSON } from '../utils/claudeProxy';

// Ett tätt CV på 13 sidor med 18 referensuppdrag ger runt 11 000 tokens JSON,
// och tänkandet ligger ovanpå det. 20 000 räckte inte – då klipptes svaret av.
const MAX_TOKENS = 32000;

const ASSIGNMENT_SCHEMA = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    period: { type: 'string' },
    role: { type: 'string' },
    client: { type: 'string' },
    budget: { type: 'string' },
    projectDescription: {
      type: 'string',
      description: 'Generell projektbeskrivning som gäller alla inblandade.',
    },
    personalDescription: {
      type: 'string',
      description: 'Konsultens specifika roll, ansvar och personliga insats.',
    },
    software: {
      type: 'array',
      items: { type: 'string' },
      description: 'Programvaror, digitala verktyg och IT-system för uppdraget.',
    },
  },
  required: ['name', 'period', 'role', 'client', 'budget',
    'projectDescription', 'personalDescription', 'software'],
  additionalProperties: false,
};

const CV_SCHEMA = {
  type: 'object',
  properties: {
    personal: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        title: { type: 'string' },
        location: { type: 'string' },
        email: { type: 'string' },
        phone: { type: 'string' },
      },
      required: ['name', 'title', 'location', 'email', 'phone'],
      additionalProperties: false,
    },
    ingress: { type: 'string' },
    competencies: { type: 'array', items: { type: 'string' } },
    assignments: { type: 'array', items: ASSIGNMENT_SCHEMA },
    employment: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          company: { type: 'string' },
          title: { type: 'string' },
          period: { type: 'string' },
          description: { type: 'string' },
        },
        required: ['company', 'title', 'period', 'description'],
        additionalProperties: false,
      },
    },
    education: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          degree: { type: 'string' },
          school: { type: 'string' },
          period: { type: 'string' },
          description: { type: 'string' },
        },
        required: ['degree', 'school', 'period', 'description'],
        additionalProperties: false,
      },
    },
    certifications: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          issuer: { type: 'string' },
          year: { type: 'string' },
        },
        required: ['name', 'issuer', 'year'],
        additionalProperties: false,
      },
    },
    tools: {
      type: 'object',
      properties: {
        expert: { type: 'array', items: { type: 'string' } },
        good: { type: 'array', items: { type: 'string' } },
        basic: { type: 'array', items: { type: 'string' } },
      },
      required: ['expert', 'good', 'basic'],
      additionalProperties: false,
    },
    languages: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          language: { type: 'string' },
          level: { type: 'string' },
        },
        required: ['language', 'level'],
        additionalProperties: false,
      },
    },
    standards: { type: 'array', items: { type: 'string' } },
    personal_profile: { type: 'array', items: { type: 'string' } },
    industries: { type: 'array', items: { type: 'string' } },
    licenses: { type: 'string' },
    board: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          role: { type: 'string' },
          org: { type: 'string' },
          period: { type: 'string' },
        },
        required: ['role', 'org', 'period'],
        additionalProperties: false,
      },
    },
  },
  required: ['personal', 'ingress', 'competencies', 'assignments', 'employment',
    'education', 'certifications', 'tools', 'languages', 'standards',
    'personal_profile', 'industries', 'licenses', 'board'],
  additionalProperties: false,
};

const INSTRUCTIONS = `Du är en expert på att tolka konsult-CV:n för svenska byggprojektföretag.

Extrahera information från detta CV enligt schemat.

Viktiga instruktioner för uppdrag/referensuppdrag:
- Varje projekt ska ha TVÅ textdelar: projectDescription (generell projektbeskrivning som gäller alla inblandade) och personalDescription (konsultens specifika roll, ansvar och personliga insats)
- Om texten blandar båda, separera dem intelligent
- projectDescription: beskriv projektet i stort (vad projektet handlar om, storlek, komplexitet, uppdragsgivare)
- personalDescription: konsultens specifika bidrag, roll, ansvar och insatser
- software: lista programvaror, digitala verktyg och IT-system som nämns för uppdraget (t.ex. AutoCAD, Revit, NovaPOINT, Bluebeam). Tom lista om inget nämns.
- Ta med ALLA projekt som nämns i CVt, även om de är många
- Om samma projekt nämns för flera medarbetare är det ett gemensamt projekt

Fält som saknas i CVt lämnas som tom sträng respektive tom lista – hitta inte på.`;

export default function CVImport({ onImport }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      let content;
      if (file.type === 'application/pdf') {
        // Claude läser PDF:en direkt. Tidigare gjordes ett extra anrop som lät
        // modellen skriva ut hela CVt som ren text först – det dubblade både
        // väntetiden och tokenåtgången utan att tillföra något.
        const base64 = await fileToBase64(file);
        content = [
          {
            type: 'document',
            source: { type: 'base64', media_type: 'application/pdf', data: base64 },
          },
          { type: 'text', text: INSTRUCTIONS },
        ];
      } else if (file.name.endsWith('.docx')) {
        const text = await extractFromWord(file);
        content = `${INSTRUCTIONS}\n\nCV att analysera:\n${text}`;
      } else {
        throw new Error('Filformat stöds ej');
      }
      const parsed = await callClaudeJSON({
        model: 'claude-opus-5',
        max_tokens: MAX_TOKENS,
        schema: CV_SCHEMA,
        messages: [{ role: 'user', content }],
      });
      onImport(pruneEmpty(parsed) || {});
      setSuccess(true);
    } catch (err) {
      console.error(err);
      // Ta med orsaken – utan den går det inte att skilja ett API-fel från
      // ett filformatsfel, och användaren har inget att rapportera vidare.
      setError('Kunde inte läsa CVt. Försök igen eller fyll i manuellt.'
        + (err?.message ? ` (${err.message})` : ''));
    } finally {
      setLoading(false);
    }
  }

  async function extractFromWord(file) {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderStyle: 'dashed', borderColor: '#1a1a1a' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <AutoFixHighIcon sx={{ color: '#1a1a1a', fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Importera befintligt CV med AI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ladda upp ditt CV som PDF eller Word så fyller AI i formuläret automatiskt
          </Typography>
        </Box>
        <Button variant="contained" component="label"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          disabled={loading}
          sx={{ bgcolor: '#1a1a1a' }}>
          {loading ? 'Analyserar...' : 'Välj PDF eller Word'}
          <input type="file" hidden accept=".pdf,.docx" onChange={handleFileUpload} />
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          CV importerat! Granska och justera informationen nedan.
        </Alert>
      )}
    </Paper>
  );
}

/**
 * Schemat kräver att modellen fyller i alla fält, så det den inte hittade
 * kommer tillbaka som "" eller []. Mottagarna spreadar in svaret över den
 * befintliga profilen, och då hade de tomma fälten skrivit över uppgifter
 * användaren redan lagt in. Plocka bort dem innan importen.
 */
function pruneEmpty(value) {
  if (Array.isArray(value)) {
    const items = value.map(pruneEmpty).filter(v => v !== undefined);
    return items.length ? items : undefined;
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      const pruned = pruneEmpty(val);
      if (pruned !== undefined) out[key] = pruned;
    }
    return Object.keys(out).length ? out : undefined;
  }
  if (typeof value === 'string') return value.trim() ? value : undefined;
  return value;
}
